import React from 'react';
import { Modal } from '@cimpress/react-components';
import { css } from 'react-emotion';
import { actions as termActions } from '../../../reducers/terms';
import { connect } from 'react-redux';
import useForm from 'react-hook-form';
import { useAsyncCallback } from 'react-async-hook';
import ErrorText from '../createContract/buyerFlow/Shared/ErrorText';
import { DISCOUNT, MARKUP } from '../../../constants/modifierType';
import { useTranslation } from 'react-i18next';

const buttonStyle = css`
  margin-top: 10px;
  margin-left: 10px;
  float: right;
`;

export function EditModifierModal({
  setLatestRevision,
  onCloseModal,
  modalOpened,
  updateDiscountPercent,
  updateMarkupPercent,
  modifierType,
  termRevision
}) {
  if (!modifierType) {
    throw new Error('modifierType is required.');
  }

  const onSubmit = data => {
    const apiCall = modifierType === DISCOUNT ? updateDiscountPercent : updateMarkupPercent;
    return apiCall(data.modifierPercent).then(({ payload }) => setLatestRevision(payload));
  };

  const initialValue = parseFloat(
    modifierType === DISCOUNT ? termRevision.modifiers.discountPercent : termRevision.modifiers.markupPercent
  );

  const { register, handleSubmit, errors: formErrors } = useForm({
    defaultValues: {
      modifierPercent: initialValue
    }
  });
  const { execute, loading, error: submitError } = useAsyncCallback(onSubmit);
  const { t } = useTranslation();

  const validationRules = {
    required: t('contractActions:requiredMessage'),
    validate: value => {
      const modifierNumber = parseFloat(value);

      if (modifierNumber === initialValue) {
        return t('contractActions:validateUniqueMessage');
      }

      const isValidAmount = modifierNumber > 0 && (modifierType === MARKUP || modifierNumber < 100);
      let error;
      if (!isValidAmount) {
        switch (modifierType) {
          case MARKUP:
            error = t('createContract:messages.markupPercentageNotGreaterThan0Message');
            break;
          case DISCOUNT:
            error = t('createContract:messages.discountPercentageMustBeBetween0And100');
            break;
          default:
            t('createContract:messages.modifierAmountNotValidMessage');
        }
      }

      return isValidAmount || error;
    }
  };

  return (
    <Modal
      show={modalOpened}
      onRequestHide={onCloseModal}
      title={t('contractActions:editModifierModal.title', { modifierType })}
      closeButton
      closeOnOutsideClick>
      <form onSubmit={handleSubmit(execute)}>
        <div className="form-group required">
          <input
            required
            className="form-control"
            name="modifierPercent"
            type="number"
            min="0"
            step="0.1"
            placeholder={12.5}
            ref={register(validationRules)}
          />
          {formErrors.modifierPercent && <ErrorText text={formErrors.modifierPercent.message} />}
        </div>
        <button className={`btn btn-primary ${buttonStyle}`} type="submit" disabled={loading}>
          Save and Update
        </button>
        <button className={`btn btn-default ${buttonStyle}`} type="button" disabled={loading} onClick={onCloseModal}>
          Cancel
        </button>
        {submitError && (
          <ErrorText text={submitError.payload ? submitError.payload.message : JSON.stringify(submitError)} />
        )}
      </form>
    </Modal>
  );
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const { termRevision } = ownProps;
  return {
    updateDiscountPercent: discountPercent =>
      dispatch(termActions.updateTermDiscountPercent(termRevision.termId, termRevision.revisionId, discountPercent)),
    updateMarkupPercent: markupPercent =>
      dispatch(termActions.updateTermMarkupPercent(termRevision.termId, termRevision.revisionId, markupPercent))
  };
};

export default connect(
  null,
  mapDispatchToProps
)(EditModifierModal);
